import { useMemo } from 'react';

import { useLocation } from '@remix-run/react';

/**
 * Extracts the pathname from a URL string, handling both absolute and relative URLs.
 * Removes search params and hash fragments.
 */
const extractPathname = (href: string): string => {
  const trimmedHref = href.trim();

  if (!trimmedHref) return '/';

  try {
    const url = new URL(trimmedHref);
    return url.pathname;
  } catch {
    const pathname = trimmedHref.split('?')[0].split('#')[0];
    return pathname || '/';
  }
};

/**
 * Normalizes a pathname by removing trailing slashes.
 * Returns '/' for empty strings.
 */
const normalizePath = (pathname: string): string => {
  if (pathname === '' || pathname === '/') return '/';
  return pathname.replace(/\/$/, '');
};

/**
 * Determines if a link is active based on exact pathname match (excluding search params).
 * @param href - The href to check against the current location
 * @returns Whether the link matches the current page
 */
export const useIsLinkActive = (href: string | undefined): boolean => {
  const location = useLocation();

  return useMemo(() => {
    if (!href || /^(mailto|tel|javascript|data|file):/i.test(href)) return false;

    const linkPathname = extractPathname(href);
    const normalizedLinkPath = normalizePath(linkPathname);
    const normalizedCurrentPath = normalizePath(location.pathname);

    return normalizedLinkPath === normalizedCurrentPath;
  }, [href, location.pathname]);
};
